import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import type { ReportData } from "../types";
import { Header, Footer, RadarChart, DataTable, GroupAnalysis } from "./components";
import { mapReportDataForTemplate } from "../utils/data-mapper";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingBottom: 60,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#1f2937"
  },
  section: {
    marginBottom: 15
  },
  introSection: {
    marginBottom: 20
  },
  introTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1e3a5f",
    marginBottom: 8
  },
  introText: {
    fontSize: 10,
    color: "#4b5563",
    lineHeight: 1.5
  },
  summaryCard: {
    backgroundColor: "#f3f4f6",
    padding: 15,
    borderRadius: 8,
    marginVertical: 15
  },
  summaryTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#374151",
    marginBottom: 10
  },
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap"
  },
  summaryItem: {
    width: "50%",
    marginBottom: 8
  },
  summaryLabel: {
    fontSize: 8,
    color: "#6b7280"
  },
  summaryValue: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#1f2937"
  },
  pageBreak: {
    marginTop: 20
  },
  groupsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1e3a5f",
    marginBottom: 15,
    marginTop: 20
  }
});

interface DefaultTemplateProperties {
  data: ReportData;
}

/**
 * DefaultTemplate - Template padrão para relatórios de diagnóstico
 *
 * Estrutura do documento:
 * 1. Cabeçalho com informações da empresa
 * 2. Introdução com descrição do questionário
 * 3. Resumo executivo com métricas principais
 * 4. Gráfico radar com resultados
 * 5. Tabela de resultados por dimensão
 * 6. Análise detalhada de cada grupo/dimensão
 * 7. Rodapé com data de geração e paginação
 * @param root0
 * @param root0.data
 */
export function DefaultTemplate({ data }: DefaultTemplateProperties) {
  const mappedData = mapReportDataForTemplate(data);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Header
          empresaNome={mappedData.empresaNome}
          empresaCnpj={mappedData.empresaCnpj}
          empresaPorte={mappedData.empresaPorte}
          questionarioNome={mappedData.questionarioNome}
          projetoNome={mappedData.projetoNome}
        />

        {/* Introduction */}
        <View style={styles.introSection}>
          <Text style={styles.introTitle}>Sobre este Diagnóstico</Text>
          <Text style={styles.introText}>
            {mappedData.questionarioDescricao ||
              "Este relatório apresenta os resultados do diagnóstico de inovação realizado para a empresa."}
          </Text>
        </View>

        {/* Executive Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryTitle}>Resumo Executivo</Text>
          <View style={styles.summaryGrid}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Média Geral</Text>
              <Text style={styles.summaryValue}>{mappedData.mediaEmpresa.toFixed(1)}%</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Dimensões Avaliadas</Text>
              <Text style={styles.summaryValue}>{mappedData.totalDimensoes}</Text>
            </View>
            {mappedData.dimensaoMaisForte && (
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Ponto Forte</Text>
                <Text style={styles.summaryValue}>
                  {mappedData.dimensaoMaisForte.segment} ({mappedData.dimensaoMaisForte.value.toFixed(1)}%)
                </Text>
              </View>
            )}
            {mappedData.dimensaoMaisFraca && (
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Oportunidade</Text>
                <Text style={styles.summaryValue}>
                  {mappedData.dimensaoMaisFraca.segment} ({mappedData.dimensaoMaisFraca.value.toFixed(1)}%)
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Radar Chart */}
        <RadarChart resultados={data.resultados.empresa} />

        {/* Results Table */}
        <DataTable resultados={data.resultados.empresa} />

        {/* Footer */}
        <Footer dataGeracao={mappedData.dataGeracao} />
      </Page>

      {/* Detailed Analysis Pages */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.groupsTitle}>Análise Detalhada por Dimensão</Text>

        {mappedData.dimensoes.map((dimensao) => (
          <GroupAnalysis
            key={dimensao.nome}
            grupoNome={dimensao.nome}
            grupoDescricao={dimensao.descricao}
            resultado={{
              segment: dimensao.nome,
              value: dimensao.resultado,
              autoAvaliacao: dimensao.autoAvaliacao
            }}
            feedbacks={dimensao.feedbacks}
          />
        ))}

        <Footer dataGeracao={mappedData.dataGeracao} />
      </Page>
    </Document>
  );
}
